import { css } from 'styled-system/css';
import { useState } from 'react';
import {
  accessCtaClass,
  ghostCtaClass,
} from '../../components/landing/AccessCta';
import { Container } from '../../components/landing/Container';
import { MarketingPage } from '../../components/landing/MarketingPage';
import { Reveal } from '../../components/landing/Reveal';
import { appSignupForProTrial } from '../../appOrigin';
import {
  BILLING_FREQUENCIES,
  type BillingFrequency,
} from '../../billing/billingFrequency';
import { paidPro } from '../../billing/paidTiers';
import { usePaddle } from '../../billing/usePaddle';
import { usePaddlePrices } from '../../billing/usePaddlePrices';
import { useViewerCountry } from '../../billing/useViewerCountry';
import { AdditionalUsage } from './AdditionalUsage';
import { BillingQuestions } from './BillingQuestions';
import { FrequencyToggle } from './FrequencyToggle';
import { PlanColumn, type PlanCopy } from './PlanColumn';

const taste = {
  name: 'Taste',
  description: 'Try the agent on your own Google Sheet.',
  price: 'No charge',
  priceDetail: 'No account, no card',
  ctaNote: 'Share a sheet with agent@exis.pro',
  includesLabel: 'Includes',
  points: [
    '25 agent cell edits per account',
    'Proposals as comments in your sheet',
    'Reading and downloading stay open after the 25 edits',
  ],
  cta: { href: '/#sheets', label: 'Invite the agent' },
};

const business = {
  name: 'Business',
  description: 'Custom volume for growing teams.',
  price: 'Custom',
  priceDetail: 'Priced on volume',
  ctaNote: 'hello@exis.pro',
  includesLabel: 'Everything in Pro, plus',
  points: [
    'Custom monthly volume',
    'SSO and seats, once sharing ships',
    'Excel for the web, later',
  ],
  cta: { href: 'mailto:hello@exis.pro', label: 'Contact us' },
};

export function Pricing() {
  const [frequency, setFrequency] = useState<BillingFrequency>('month');
  const country = useViewerCountry();
  const paddle = usePaddle();
  const { prices, loading } = usePaddlePrices(paddle, country);
  const priceId = paidPro.priceId[frequency];
  const listed = priceId ? prices[priceId] : undefined;
  const proPrice = loading
    ? '…'
    : (listed ?? (frequency === 'month' ? '20 USD' : '—'));
  const freqMeta = BILLING_FREQUENCIES.find((f) => f.value === frequency)!;
  const proTrialHref = appSignupForProTrial(frequency);
  const pro: PlanCopy = {
    name: paidPro.name,
    description: paidPro.description,
    price: proPrice,
    priceDetail: freqMeta.detail,
    ctaNote: '14 day trial, card required',
    includesLabel: 'Everything in Taste, plus',
    points: paidPro.features,
  };

  return (
    <MarketingPage>
      <section
        aria-label="Pricing"
        className={css({ paddingTop: { base: '32', md: '40' } })}
      >
        <Container>
          <Reveal>
            <div
              className={css({
                display: 'flex',
                flexDirection: 'column',
                gap: '5',
                maxWidth: '[720px]',
              })}
            >
              <span
                className={css({
                  textStyle: 'eyebrow',
                  color: 'text.muted',
                })}
              >
                Pricing
              </span>
              <h1
                className={css({
                  textStyle: 'display',
                  fontSize: { base: '4xl', md: '5xl' },
                  letterSpacing: 'tight',
                  color: 'text.primary',
                  textWrap: 'balance',
                  margin: '0',
                })}
              >
                Priced by the cell
              </h1>
              <p
                className={css({
                  fontSize: { base: 'base', md: 'md' },
                  color: 'text.secondary',
                  lineHeight: 'relaxed',
                  textWrap: 'pretty',
                  maxWidth: '[50ch]',
                })}
              >
                Start free by inviting the agent to a Google Sheet. Go Pro when
                you want it working every day. Reading, downloading, and History
                stay open.
              </p>
            </div>
          </Reveal>

          <Reveal delay={80}>
            <div
              className={css({
                display: 'grid',
                gridTemplateColumns: {
                  base: '1fr',
                  md: 'repeat(3, minmax(0, 1fr))',
                },
                marginTop: { base: '10', md: '14' },
                border: '1px solid token(colors.border.hairline)',
                background: 'bg.surface',
              })}
            >
              <PlanColumn
                plan={taste}
                featured={false}
                cta={
                  <a href={taste.cta.href} className={ghostCtaClass('md')}>
                    {taste.cta.label}
                  </a>
                }
              />
              <PlanColumn
                plan={pro}
                featured
                control={
                  <FrequencyToggle
                    frequency={frequency}
                    onSelect={setFrequency}
                  />
                }
                cta={
                  <a
                    href={proTrialHref}
                    data-testid="pro-checkout"
                    className={accessCtaClass('md')}
                  >
                    Start Pro trial
                  </a>
                }
              />
              <PlanColumn
                plan={business}
                featured={false}
                cta={
                  <a href={business.cta.href} className={ghostCtaClass('md')}>
                    {business.cta.label}
                  </a>
                }
              />
              <AdditionalUsage />
            </div>
          </Reveal>
        </Container>
      </section>
      <BillingQuestions />
    </MarketingPage>
  );
}
